import { useState, useRef, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  User,
  LogOut,
  ChevronDown,
  Loader2,
  ShieldCheck,
  ChevronRight,
  X,
  KeyRound,
  Gift,
  LifeBuoy,
  Moon,
} from "lucide-react";
import axiosInstance, { getAuthToken } from "@/utils/appsettings";
import { onCreditUpdate, emitCreditUpdate } from "@/utils/creditEvents";

import { UniversalKeyModal } from '@/components/UniversalKeyModal';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { getWorkspaceQueryString } from "@/utils/workspaceUtils";
import { fetchBillingSummary } from "@/lib/billingSummary";
interface UserProfileMenuProps {
  hideName?: boolean;
  darkBackground?: boolean;
  sidebarAvatar?: boolean;
  hasNotifications?: boolean;
}

export const UserProfileMenu = ({ hideName = false, darkBackground = false, sidebarAvatar = false, hasNotifications = false }: UserProfileMenuProps) => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Workspace Context
  const { activeWorkspace, isLoading: workspaceLoading } = useWorkspace();


  /* ---------------- CREDIT BALANCE FETCH ---------------- */
  // Balance is not displayed here any more, but this fetch still broadcasts credit updates the chat credit bar listens for
  const [, setLoadingCredits] = useState(true);
  const [, setCreditsError] = useState(false);

  const [, setPlanName] = useState("—");
  const [, setAllocations] = useState<any[]>([]);
  const [, setUsageSummary] = useState<any>({});



  const [showUniversalKeyModal, setShowUniversalKeyModal] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    // emitOnSuccess is false for event-triggered fetches so we don't bounce the update back and loop.
    const fetchCredits = async (emitOnSuccess: boolean = true) => {
      try {
        setLoadingCredits(true);
        // Reset balances immediately so old workspace data doesn't bleed while loading
        setPlanName("—");
        setAllocations([]);
        setUsageSummary({});
        setCreditsError(false);

        const data = await fetchBillingSummary(getWorkspaceQueryString(activeWorkspace), { token: getAuthToken() });

        const sub = data.subscription;
        const usage = data.usage_summary || {};

        setPlanName(sub?.plan || "Free");
        setUsageSummary(usage);
        setAllocations(data.allocations || []);

        // Save the full usage summary for the tooltip
        setUsageSummary(usage);

        setCreditsError(false);

        // Broadcast the fresh balance so the chat credit bar refetches and stays in sync, unless this fetch was itself event-triggered.
        if (emitOnSuccess) {
          emitCreditUpdate("profile-menu");
        }
      } catch (err) {
        setCreditsError(true);
      } finally {
        setLoadingCredits(false);
      }
    };

    // Initial fetch on mount or workspace change, as long as it's not loading
    if (!workspaceLoading && activeWorkspace?.id) {
      fetchCredits();
    }

    // Listen for credit update events from other components; ignore our own, and don't re-emit to avoid a refetch loop.
    const cleanup = onCreditUpdate((source) => {
      if (source === "profile-menu") return;
      if (!workspaceLoading && activeWorkspace?.id) {
        fetchCredits(false);
      }
    });

    // Cleanup event listener on unmount
    return cleanup;
  }, [isOpen, activeWorkspace, workspaceLoading]); // Add isOpen as dependency to refresh when menu opens

  // Removed manual click outside listener as DropdownMenu handles this

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      toast({
        title: "Goodbye!",
        description: "You've been successfully logged out.",
      });
      navigate("/login");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Logout Failed",
        description: "There was an issue logging you out. Please try again.",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  if (!user) {
    return null;
  }

  const closeThen = (fn: () => void) => () => {
    setIsOpen(false);
    fn();
  };

  return (
    <div className="relative" ref={menuRef}>
      {hideName ? (
        // Compact account trigger; sidebar uses a smaller version of the themed avatar.
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={sidebarAvatar && hasNotifications ? "Your account, unread notifications" : "Your account"}
          onClick={() => setIsOpen(true)}
          className={`relative grid shrink-0 place-items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plum/40 ${sidebarAvatar ? "h-7 w-7 border-line bg-selected text-[10px] font-semibold text-plum hover:border-plum/40" : "h-9 w-9 border-line bg-selected text-[11px] font-semibold text-plum hover:border-plum/40"}`}
        >
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="h-full w-full rounded-full object-cover" />
          ) : (
            getInitials(user.name || user.email || "J")
          )}
          {sidebarAvatar && hasNotifications && (
            <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border border-surface bg-plum" />
          )}
        </button>
      ) : (
      <Button
          variant="ghost"
          size="sm"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(true)}
          className={`flex items-center gap-2 py-6 rounded-xl transition-colors ${darkBackground ? "hover:bg-zinc-700" : "hover:bg-soft"}`}
        >
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-sunset flex items-center justify-center text-white text-[11px] sm:text-xs font-semibold flex-shrink-0">
              {getInitials(user.name)}
            </div>
          )}
          {!hideName && (
            <div className="hidden sm:flex flex-col items-start min-w-0">
              <span className={`text-sm font-medium truncate max-w-[150px] ${darkBackground ? "text-white" : "text-foreground"}`}>
                {user.name}
              </span>
              <span className={`text-xs truncate max-w-[150px] ${darkBackground ? "text-gray-300" : "text-muted-foreground"}`}>
                {user.email}
              </span>
            </div>
          )}
          <ChevronDown className={`h-4 w-4 flex-shrink-0 ${darkBackground ? "text-gray-400" : "text-muted-foreground"}`} />
        </Button>
      )}

      <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
        <DialogPrimitive.Portal>
          {/* z-[10020]: must clear the account/project SettingsShell overlays at z-[10010] */}
          <DialogPrimitive.Overlay className="fixed inset-0 z-[10020] bg-[#1d1626]/45 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            aria-describedby="account-dialog-description"
            className="fixed left-1/2 top-1/2 z-[10020] flex w-[calc(100vw-2rem)] max-w-[560px] max-h-[min(90dvh,820px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[18px] border border-line bg-surface text-foreground shadow-[0_24px_64px_rgba(29,22,38,0.22)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
            <div className="flex shrink-0 items-start justify-between gap-4 px-6 pt-6 pb-4 sm:px-8 sm:pt-8">
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="min-w-0">
                  <DialogPrimitive.Title className="font-display text-[24px] font-semibold leading-tight tracking-[-0.03em] text-foreground sm:text-[28px]">
                    Your account
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description id="account-dialog-description" className="mt-1.5 text-[14px] leading-snug text-muted-foreground">
                    {user.name} · Account settings apply across your workspaces
                  </DialogPrimitive.Description>
                </div>
              </div>
              <DialogPrimitive.Close
                aria-label="Close"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-line text-muted-foreground transition-colors hover:border-plum/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plum/50"
              >
                <X className="h-[18px] w-[18px]" />
              </DialogPrimitive.Close>
            </div>

            <nav aria-label="Account settings" className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 sm:px-8">
              <MenuRow icon={<User />} text="My profile" onClick={closeThen(() => navigate("/profile", { state: { background: location } }))} />
              <MenuRow icon={<ShieldCheck />} text="Security" onClick={closeThen(() => navigate("/security", { state: { background: location } }))} />
              <MenuRow icon={<KeyRound />} text="Universal AI Key" onClick={closeThen(() => navigate("/universal-ai-key", { state: { returnTo: location } }))} />
              <MenuRow icon={<Gift />} text="Share Joylo" onClick={closeThen(() => navigate("/referrals", { state: { returnTo: location } }))} />
              <MenuRow icon={<LifeBuoy />} text="Support requests" onClick={closeThen(() => navigate("/my-tickets", { state: { background: location } }))} />
              {/* Toggles in place rather than closing, so the user sees the theme change behind the dialog */}
              <label className="flex min-h-[46px] w-full cursor-pointer items-center gap-3.5 rounded-lg px-2 transition-colors hover:bg-soft">
                <span className="shrink-0 text-foreground/80 [&_svg]:h-[19px] [&_svg]:w-[19px]"><Moon /></span>
                <span className="min-w-0 flex-1 text-[15px] font-medium text-foreground">Dark mode</span>
                <Switch
                  checked={isDark}
                  onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
                  aria-label="Dark mode"
                />
              </label>
            </nav>

            <div className="mx-6 shrink-0 border-t border-line py-3 sm:mx-8">
              <button
                type="button"
                className="flex min-h-[44px] w-full items-center rounded-lg px-2 text-[15px] font-medium text-danger transition-colors hover:bg-danger-bg"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? <Loader2 className="mr-3.5 h-[19px] w-[19px] animate-spin" /> : <LogOut className="mr-3.5 h-[19px] w-[19px]" />}
                {isLoggingOut ? "Signing out..." : "Sign out"}
              </button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      {/* Universal AI Key Modal */}
      <UniversalKeyModal
        isOpen={showUniversalKeyModal}
        onClose={() => setShowUniversalKeyModal(false)}
      />
    </div>

  );

  // One account-list row: icon, label, chevron
  function MenuRow({ icon, text, onClick, accent = false }: { icon: React.ReactNode; text: React.ReactNode; onClick: () => void; accent?: boolean }) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group flex min-h-[46px] w-full items-center gap-3.5 rounded-lg px-2 text-left transition-colors hover:bg-soft"
      >
        <span className={`shrink-0 [&_svg]:h-[19px] [&_svg]:w-[19px] ${accent ? "text-plum" : "text-foreground/80"}`}>{icon}</span>
        <span className={`min-w-0 flex-1 text-[15px] text-foreground ${accent ? "font-semibold" : "font-medium"}`}>{text}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </button>
    );
  }

};